import { apiClient, handleApiRequest } from './client'
import type {
  QualificationEntry,
  Background,
  Subject,
  ImportPreview,
  ImportResult,
  EligibleTeachersResponse,
  QualificationLookupResponse,
  BatchDetail,
  MatrixScanResponse,
  MatrixMapping,
  SubjectQualificationGroup,
  SubjectQualificationGroupListResponse,
} from '@/types/qualification'

const BASE = '/api/v1/qualification-matrix'
const TEACHER_ADMIN = '/api/v1/teachers'

export async function listQualifications(params?: {
  background_id?: string
  subject_id?: string
  qualification_type?: string
  group_name?: string
  include_inactive?: boolean
}): Promise<QualificationEntry[]> {
  const search = new URLSearchParams()
  if (params?.background_id) search.set('background_id', params.background_id)
  if (params?.subject_id) search.set('subject_id', params.subject_id)
  if (params?.qualification_type) search.set('qualification_type', params.qualification_type)
  if (params?.group_name) search.set('group_name', params.group_name)
  if (params?.include_inactive) search.set('include_inactive', 'true')
  const query = search.toString()
  return handleApiRequest(apiClient.get(`${BASE}${query ? `?${query}` : ''}`))
}

export async function getQualification(id: string): Promise<QualificationEntry> {
  return handleApiRequest(apiClient.get(`${BASE}/${id}`))
}

export async function createQualification(data: {
  background_id: string
  subject_id: string
  qualification_type: 'qualified' | 'group'
  group_name?: string | null
  priority?: number
  requires_same_class_year?: boolean
  class_year?: string | null
  notes?: string | null
}): Promise<QualificationEntry> {
  return handleApiRequest(apiClient.post(BASE, data))
}

export async function updateQualification(
  id: string,
  data: {
    qualification_type?: 'qualified' | 'group'
    group_name?: string | null
    priority?: number
    requires_same_class_year?: boolean
    class_year?: string | null
    notes?: string | null
    is_verified?: boolean
  }
): Promise<QualificationEntry> {
  return handleApiRequest(apiClient.patch(`${BASE}/${id}`, data))
}

export async function deleteQualification(id: string): Promise<{ message: string }> {
  return handleApiRequest(apiClient.delete(`${BASE}/${id}`))
}

export async function listBackgrounds(includeInactive = false, matrixHeaders = false): Promise<Background[]> {
  return handleApiRequest(
    apiClient.get(
      `${TEACHER_ADMIN}/backgrounds?include_inactive=${includeInactive}&matrix_headers=${matrixHeaders}`,
    )
  )
}

export async function createBackground(data: {
  name: string
  code?: string | null
  description?: string | null
}, matrixHeader = false): Promise<Background> {
  return handleApiRequest(apiClient.post(
    `${TEACHER_ADMIN}/backgrounds?matrix_header=${matrixHeader}`,
    data,
  ))
}

export async function listSubjects(includeInactive = false, matrixHeaders = false): Promise<Subject[]> {
  return handleApiRequest(
    apiClient.get(
      `${TEACHER_ADMIN}/subjects?include_inactive=${includeInactive}&matrix_headers=${matrixHeaders}`,
    )
  )
}

export async function setSubjectSubstitutionExcluded(
  id: string,
  excluded: boolean,
): Promise<Subject> {
  return handleApiRequest(apiClient.patch(
    `${TEACHER_ADMIN}/subjects/${id}/substitution-excluded`,
    null,
    { params: { excluded } },
  ))
}

export async function setSubjectQualificationOverride(
  id: string,
  override: 'cells' | 'all' | 'none',
): Promise<Subject> {
  return handleApiRequest(apiClient.patch(
    `${TEACHER_ADMIN}/subjects/${id}/qualification-override`,
    null,
    { params: { override } },
  ))
}

export async function createSubject(data: {
  name: string
  code?: string | null
  description?: string | null
  class_year?: string | null
}, matrixHeader = false): Promise<Subject> {
  return handleApiRequest(apiClient.post(
    `${TEACHER_ADMIN}/subjects?matrix_header=${matrixHeader}`,
    data,
  ))
}

export async function previewImport(
  uploaded_file_id: string,
  mapping?: MatrixMapping
): Promise<ImportPreview> {
  const body: Record<string, unknown> = { uploaded_file_id }
  if (mapping) body.mapping = mapping
  return handleApiRequest(
    apiClient.post(`${BASE}/import/preview`, body)
  )
}

export async function executeImport(
  uploaded_file_id: string,
  batch_name?: string,
  mapping?: MatrixMapping
): Promise<ImportResult> {
  const body: Record<string, unknown> = { uploaded_file_id }
  if (batch_name) body.batch_name = batch_name
  if (mapping) body.mapping = mapping
  return handleApiRequest(
    apiClient.post(`${BASE}/import/execute`, body)
  )
}

export async function scanMatrix(uploaded_file_id: string): Promise<MatrixScanResponse> {
  return handleApiRequest(
    apiClient.post(`${BASE}/import/scan`, { uploaded_file_id })
  )
}

export async function getBatchDetails(batch_id: string): Promise<BatchDetail> {
  return handleApiRequest(apiClient.get(`${BASE}/import/batch/${batch_id}`))
}

export async function exportMatrix(format: 'xlsx' | 'csv' = 'xlsx'): Promise<Blob> {
  const response = await apiClient.post(
    `${BASE}/export`,
    { format },
    { responseType: 'blob' }
  )
  return response.data as Blob
}

export async function getQualifiedSubjects(background_id: string): Promise<QualificationLookupResponse> {
  return handleApiRequest(apiClient.get(`${BASE}/lookup/${background_id}`))
}

export async function getEligibleTeachers(subject_id: string): Promise<EligibleTeachersResponse> {
  return handleApiRequest(apiClient.get(`${BASE}/eligible-teachers/${subject_id}`))
}

export async function bulkDeleteMatrix(
  confirm: boolean,
  deleteBackgrounds = false,
  deleteSubjects = false
): Promise<{ success: boolean; entries_deleted: number; backgrounds_deleted: number; subjects_deleted: number; groups_deleted: number; aliases_deleted: number }> {
  return handleApiRequest(
    apiClient.delete(`${BASE}/import/bulk`, {
      data: { confirm, delete_backgrounds: deleteBackgrounds, delete_subjects: deleteSubjects },
    })
  )
}

export async function bulkDeleteSelectedHeaders(
  backgroundIds: string[],
  subjectIds: string[],
): Promise<{
  success: boolean
  backgrounds_deleted: number
  subjects_deleted: number
  entries_deleted: number
  groups_deleted: number
  aliases_deleted: number
}> {
  return handleApiRequest(
    apiClient.delete(`${BASE}/headers/bulk`, {
      data: { confirm: true, background_ids: backgroundIds, subject_ids: subjectIds },
    })
  )
}

export async function deleteBackground(id: string): Promise<{ message: string; id: string }> {
  return handleApiRequest(apiClient.delete(`${TEACHER_ADMIN}/backgrounds/${id}`))
}

export async function deleteSubject(id: string): Promise<{ message: string; id: string }> {
  return handleApiRequest(apiClient.delete(`${TEACHER_ADMIN}/subjects/${id}`))
}

export async function listSubjectAliases(): Promise<Array<{ id: string; alias_name: string; canonical_subject_id: string; source_type: string; is_confirmed: boolean }>> {
  return handleApiRequest(apiClient.get('/api/v1/subjects/aliases'))
}

export async function listBackgroundAliases(): Promise<Array<{ id: string; alias_name: string; canonical_background_id: string; source_type: string; is_confirmed: boolean }>> {
  return handleApiRequest(apiClient.get('/api/v1/backgrounds/aliases'))
}

export async function createSubjectAlias(data: {
  canonical_subject_id: string
  alias_name: string
  source_type?: string
  is_confirmed?: boolean
}): Promise<{ id: string; alias_name: string; canonical_subject_id: string; source_type: string; is_confirmed: boolean }> {
  return handleApiRequest(apiClient.post('/api/v1/subjects/aliases', {
    canonical_subject_id: data.canonical_subject_id,
    alias_name: data.alias_name,
    source_type: data.source_type || 'manual',
    is_confirmed: data.is_confirmed ?? true,
    confidence: 1.0,
  }))
}

export async function deleteSubjectAlias(aliasId: string): Promise<{ message: string }> {
  return handleApiRequest(apiClient.delete(`/api/v1/subjects/aliases/${aliasId}`))
}

export async function createBackgroundAlias(data: {
  canonical_subject_id: string
  alias_name: string
  source_type?: string
  is_confirmed?: boolean
}): Promise<{ id: string; alias_name: string; canonical_subject_id: string; source_type: string; is_confirmed: boolean }> {
  return handleApiRequest(apiClient.post('/api/v1/backgrounds/aliases', {
    canonical_subject_id: data.canonical_subject_id,
    alias_name: data.alias_name,
    source_type: data.source_type || 'manual',
    is_confirmed: data.is_confirmed ?? true,
    confidence: 1.0,
  }))
}

export async function deleteBackgroundAlias(aliasId: string): Promise<{ message: string }> {
  return handleApiRequest(apiClient.delete(`/api/v1/backgrounds/aliases/${aliasId}`))
}

// ===========================================
// GROUP API FUNCTIONS
// ===========================================

export async function createQualificationGroup(data: {
  subject_id: string
  name: string
  description?: string | null
  match_mode?: 'or' | 'and'
  member_background_ids: string[]
}): Promise<SubjectQualificationGroup> {
  return handleApiRequest(apiClient.post(`${BASE}/groups`, data))
}

export async function listQualificationGroups(params?: {
  subject_id?: string
  include_inactive?: boolean
}): Promise<SubjectQualificationGroupListResponse> {
  const search = new URLSearchParams()
  if (params?.subject_id) search.set('subject_id', params.subject_id)
  if (params?.include_inactive) search.set('include_inactive', 'true')
  const query = search.toString()
  return handleApiRequest(apiClient.get(`${BASE}/groups${query ? `?${query}` : ''}`))
}

export async function getQualificationGroup(id: string): Promise<SubjectQualificationGroup> {
  return handleApiRequest(apiClient.get(`${BASE}/groups/${id}`))
}

export async function updateQualificationGroup(
  id: string,
  data: {
    name?: string
    description?: string | null
    match_mode?: 'or' | 'and'
    member_background_ids?: string[]
  }
): Promise<SubjectQualificationGroup> {
  return handleApiRequest(apiClient.patch(`${BASE}/groups/${id}`, data))
}

export async function deleteQualificationGroup(id: string): Promise<{ message: string }> {
  return handleApiRequest(apiClient.delete(`${BASE}/groups/${id}`))
}

export async function addGroupMember(groupId: string, backgroundId: string): Promise<{ message: string }> {
  return handleApiRequest(apiClient.post(`${BASE}/groups/${groupId}/members`, null, {
    params: { background_id: backgroundId }
  }))
}

export async function removeGroupMember(groupId: string, backgroundId: string): Promise<{ message: string }> {
  return handleApiRequest(apiClient.delete(`${BASE}/groups/${groupId}/members/${backgroundId}`))
}

export async function getSubjectGroups(subjectId: string): Promise<SubjectQualificationGroupListResponse> {
  return handleApiRequest(apiClient.get(`${BASE}/subjects/${subjectId}/groups`))
}

export async function reorderGroups(data: {
  subject_id: string
  name_map: Record<string, string>
}): Promise<{ message: string }> {
  return handleApiRequest(apiClient.post(`${BASE}/groups/reorder`, data))
}
